import { APP_CONFIG } from '@/lib/config/api.config';
import { metricsService } from '@/lib/services/metrics.service';
import { logger } from '@/lib/utils/logger';
import { Session } from 'next-auth';

// Global kill switch for metrics tracking
// Set to false to enable metrics tracking
export const DISABLE_ALL_METRICS = false;

// Enum for metric categories
export enum MetricsCategory {
  USER_ACTIVITY = 'UserActivity',
  QUESTION = 'Question',
  CASELAW = 'Caselaw',
  FEEDBACK = 'Feedback',
  INTERACTION = 'Interaction',
  PERFORMANCE = 'Performance',
  ERROR = 'Error',
  CONVERSATION = 'Conversation',
  RESPONSE_TIME = 'ResponseTime', // Added to match backend
}

// Enum for metric actions
export enum MetricsAction {
  // User Activity
  LOGIN = 'Login',
  LOGOUT = 'Logout',
  SIGNUP = 'Signup',
  PROFILE_UPDATE = 'ProfileUpdate',
  SESSION_STARTED = 'SessionStarted',
  SESSION_ENDED = 'SessionEnded',

  // Questions
  QUESTION_ASKED = 'QuestionAsked',
  QUESTION_ANSWERED = 'QuestionAnswered',
  LAMBDA_PROMPT_ASKED = 'LambdaPromptAsked',
  // Feedback
  FEEDBACK_SUBMITTED = 'FeedbackSubmitted',
  RATING_SUBMITTED = 'RatingSubmitted',

  //Caselaw search
  CASELAW_SUBMISSION_STARTED = 'CaselawSubmissionStart',
  CASELAW_SUBMISSION_SUCCESS = 'CaselawSubmissionSuccess',
  CASELAW_SUBMISSION_FAILED = 'CaselawSubmissionFailed',
  // User Activity
  EDIT = 'Edit',

  // Interactions
  PAGE_VIEW = 'PageView',
  BUTTON_CLICK = 'ButtonClick',
  FEATURE_USED = 'FeatureUsed',
  SELECTION = 'Selection',

  // Performance
  PAGE_LOAD = 'PageLoad',
  API_RESPONSE = 'ApiResponse',
  LAMBDA_RESPONSE = 'LambdaResponse',
  LAMBDA_RESPONSE_TIME = 'LambdaResponseTime', // Added to match backend
  FRONTEND_RESPONSE = 'FrontendResponse',
  FRONTEND_RESPONSE_TIME = 'FrontendResponseTime', // Added to match backend

  // Errors
  CLIENT_ERROR = 'ClientError',
  SERVER_ERROR = 'ServerError',

  // Conversation
  CONVERSATION_STARTED = 'ConversationStarted',
  CONVERSATION_MESSAGE = 'ConversationMessage',
  CONVERSATION_ENDED = 'ConversationEnded',
}

// Function to get device and browser information
function getDeviceInfo() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;

    // Simple device type detection
    let deviceType = 'Desktop';
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)) {
      deviceType = 'Mobile';
      if (/iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent)) {
        deviceType = 'Tablet';
      }
    }

    // Browser detection
    let browser = 'Unknown';
    let browserVersion = '';

    if (
      /Chrome/i.test(userAgent) &&
      !/Chromium|Edge|OPR|Edg/i.test(userAgent)
    ) {
      browser = 'Chrome';
      browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
    } else if (/Firefox/i.test(userAgent)) {
      browser = 'Firefox';
      browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
    } else if (
      /Safari/i.test(userAgent) &&
      !/Chrome|Chromium|Edge|OPR|Edg/i.test(userAgent)
    ) {
      browser = 'Safari';
      browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || '';
    } else if (/Edge|Edg/i.test(userAgent)) {
      browser = 'Edge';
      browserVersion =
        userAgent.match(/Edge\/(\d+\.\d+)|Edg\/(\d+\.\d+)/)?.[1] || '';
    } else if (/OPR/i.test(userAgent)) {
      browser = 'Opera';
      browserVersion = userAgent.match(/OPR\/(\d+\.\d+)/)?.[1] || '';
    }

    // OS detection
    let os = 'Unknown';
    let osVersion = '';

    if (/Windows/i.test(userAgent)) {
      os = 'Windows';
      osVersion = userAgent.match(/Windows NT (\d+\.\d+)/)?.[1] || '';
    } else if (/Macintosh/i.test(userAgent)) {
      os = 'macOS';
      osVersion =
        userAgent.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
    } else if (/Android/i.test(userAgent)) {
      os = 'Android';
      osVersion = userAgent.match(/Android (\d+\.\d+)/)?.[1] || '';
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      os = 'iOS';
      osVersion =
        userAgent.match(/OS (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
    } else if (/Linux/i.test(platform)) {
      os = 'Linux';
    }

    return {
      deviceType,
      browser,
      browserVersion,
      os,
      osVersion,
    };
  } catch (error) {
    // If anything goes wrong, return empty object
    logger.debug('Error getting device info', { error });
    return {};
  }
}

// Function to get a unique session ID
function getSessionId() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return '';
  }

  try {
    let sessionId = sessionStorage.getItem('metrics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('metrics_session_id', sessionId);
    }

    return sessionId;
  } catch (error) {
    // If anything goes wrong, return empty string
    logger.debug('Error getting session ID', { error });
    return '';
  }
}

// Main function to track metrics events (queues for batching)
export function trackMetricsEvent({
  category,
  action,
  label = '',
  value = 0,
  userId = '',
  dimensions = {},
}: {
  category: MetricsCategory | string;
  action: MetricsAction | string;
  label?: string;
  value?: number;
  userId?: string;
  dimensions?: Record<string, any>;
}) {
  // If metrics are globally disabled, return immediately
  if (DISABLE_ALL_METRICS) {
    return;
  }

  // Use a non-blocking approach using the service which handles batching
  try {
    // Access the isAuthenticating flag from the usePageViewTracking hook
    // @ts-ignore - This is defined in usePageViewTracking.ts
    if (typeof window !== 'undefined' && window.isAuthenticating === true) {
      logger.debug('Skipping metrics during authentication transition');
      return;
    }

    // The service now handles session checking internally before queuing
    const effectiveUserId = userId;
    const sessionId = getSessionId();
    const deviceInfo = getDeviceInfo();

    metricsService.trackEvent({
      category,
      action,
      label,
      value,
      userId: effectiveUserId,
      sessionId,
      ...deviceInfo,
      dimensions,
    });
  } catch (error) {
    // Log the error but don't let it disrupt the application flow
    logger.error('Failed to queue metrics event', {
      error,
      category,
      action,
    });
  }

  // Return immediately without waiting for the tracking to complete
  return;
}

// Helper function to track page views
export function trackPageView(
  pageName: string,
  userId: string,
  groupName: string
) {
  // If metrics are globally disabled, return immediately
  if (DISABLE_ALL_METRICS) {
    return;
  }
  // const { user } = useUser();
  trackMetricsEvent({
    category: MetricsCategory.INTERACTION,
    action: MetricsAction.PAGE_VIEW,
    label: pageName,
    userId: userId,
    dimensions: { groupName: groupName },
  });
}

// Helper function to track button clicks
export function trackButtonClick(buttonName: string) {
  // If metrics are globally disabled, return immediately
  if (DISABLE_ALL_METRICS) {
    return;
  }

  trackMetricsEvent({
    category: MetricsCategory.INTERACTION,
    action: MetricsAction.BUTTON_CLICK,
    label: buttonName,
  });
}

// Helper function to track feature usage
export function trackFeatureUsed(featureName: string) {
  // If metrics are globally disabled, return immediately
  if (DISABLE_ALL_METRICS) {
    return;
  }

  trackMetricsEvent({
    category: MetricsCategory.INTERACTION,
    action: MetricsAction.FEATURE_USED,
    label: featureName,
  });
}

// Helper function to track questions
export function trackQuestion(questionText: string, topic: string) {
  // If metrics are globally disabled, return immediately
  if (DISABLE_ALL_METRICS) {
    return;
  }

  trackMetricsEvent({
    category: MetricsCategory.QUESTION,
    action: MetricsAction.QUESTION_ASKED,
    label: questionText.substring(0, 100), // Limit length
    dimensions: { topic },
  });
}

// Helper function to track feedback
export function trackFeedback(rating: number, comment: string = '') {
  // If metrics are globally disabled, return immediately
  if (DISABLE_ALL_METRICS) {
    return;
  }

  // Determine sentiment based on rating
  const sentiment = rating > 3 ? 'positive' : 'negative';

  trackMetricsEvent({
    category: MetricsCategory.FEEDBACK,
    action: MetricsAction.FEEDBACK_SUBMITTED,
    value: rating,
    label: comment.substring(0, 100), // Limit length
    dimensions: {
      sentiment,
      rating,
      hasComment: comment.length > 0,
      reason: comment ? 'User provided' : 'Quick rating',
    },
  });
}

// Helper function to track errors
export function trackError(
  errorType: 'client' | 'server',
  errorMessage: string
) {
  // If metrics are globally disabled, return immediately
  if (DISABLE_ALL_METRICS) {
    return;
  }

  trackMetricsEvent({
    category: MetricsCategory.ERROR,
    action:
      errorType === 'client'
        ? MetricsAction.CLIENT_ERROR
        : MetricsAction.SERVER_ERROR,
    label: errorMessage.substring(0, 200), // Limit length
  });
}

/**
 * Helper function to track session start events.
 * Should be called on successful login or initial load with valid session.
 */
export function trackSessionStart() {
  if (DISABLE_ALL_METRICS) {
    return;
  }
  trackMetricsEvent({
    category: MetricsCategory.USER_ACTIVITY,
    action: MetricsAction.SESSION_STARTED,
    // Optionally add dimensions like entry point URL
    dimensions: {
      entryPoint: typeof window !== 'undefined' ? window.location.pathname : '',
    },
  });
}

/**
 * Helper function to track session end events.
 * Should be called on logout or potentially before unload.
 */
export function trackSessionEnd() {
  if (DISABLE_ALL_METRICS) {
    return;
  }
  trackMetricsEvent({
    category: MetricsCategory.USER_ACTIVITY,
    action: MetricsAction.SESSION_ENDED,
  });
  // Attempt to send any remaining batched events immediately
  // Note: This might not always succeed before the page unloads
  metricsService.flushQueue();
}
